import type { AdminAsset, AssetUsageLocation } from '@diana/contracts';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Button } from '../../ui/index.js';
import { MediaPreview } from './MediaCard.js';
import { MediaHeader, type MediaActions } from './MediaLibrary.js';

import { TextAreaField } from '../settings/SettingsEditor.js';

export function MediaItem({
  asset: initialAsset,
  loadUsages,
  updateAsset,
  archiveAsset,
}: { asset: AdminAsset } & MediaActions) {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [initialAsset.id]);
  const [asset, setAsset] = useState(initialAsset);
  const [draft, setDraft] = useState(initialAsset);
  const authoritative = JSON.stringify(initialAsset);
  const previousAuthoritative = useRef(authoritative);
  const actionPending = useRef(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [usages, setUsages] = useState<AssetUsageLocation[] | null>(null);
  const [usagesOpen, setUsagesOpen] = useState(false);
  const [usagesBusy, setUsagesBusy] = useState(false);
  const [usagesError, setUsagesError] = useState(false);
  useEffect(() => {
    if (previousAuthoritative.current === authoritative) return;
    previousAuthoritative.current = authoritative;
    setAsset(initialAsset);
    setDraft(initialAsset);
  }, [authoritative, initialAsset]);
  const onChange = (updated: AdminAsset) => {
    setAsset(updated);
    setDraft(updated);
  };

  const save = async () => {
    if (actionPending.current || !draft.alt.trim()) return;
    actionPending.current = true;
    setBusy(true);
    setMessage(null);
    try {
      const updated = await updateAsset(asset.id, {
        expectedRevision: asset.revision,
        alt: draft.alt,
      });
      onChange(updated);
      setEditing(false);
      setMessage('Media details saved.');
    } catch {
      setMessage('Media details could not be saved. Your edits remain here.');
    } finally {
      actionPending.current = false;
      setBusy(false);
    }
  };

  const archive = async () => {
    if (actionPending.current || editing || asset.archivedAt) return;
    actionPending.current = true;
    setBusy(true);
    setMessage(null);
    try {
      const updated = await archiveAsset(asset.id, asset.revision);
      onChange(updated);
      setMessage('Archived. Existing page and preview references remain intact.');
    } catch {
      setMessage('The asset could not be archived.');
    } finally {
      actionPending.current = false;
      setBusy(false);
    }
  };

  const toggleUsages = async () => {
    if (usagesOpen) {
      setUsagesOpen(false);
      return;
    }
    setUsagesOpen(true);
    if (usages !== null || usagesBusy) return;
    setUsagesBusy(true);
    setUsagesError(false);
    try {
      setUsages(await loadUsages(asset.id));
    } catch {
      setUsagesError(true);
    } finally {
      setUsagesBusy(false);
    }
  };

  return (
    <article className="editor media-item" data-archived={asset.archivedAt ? 'true' : undefined}>
      <MediaHeader
        title="Photo"
        backTo={`/settings/media${location.search}`}
        backLabel="Back to media"
      />
      <div className="media-item__layout">
        <div className="media-item__visual">
          <MediaPreview asset={asset} />
          <span className="media-item__image-meta">
            {asset.format?.toUpperCase() ?? asset.sourceType} · {asset.width ?? '—'} ×{' '}
            {asset.height ?? '—'}
          </span>
        </div>
        <div className="media-item__body">
          <div className="media-item__meta">
            <span className="media-item__badge">
              {asset.status === 'Ready' ? 'Uploaded' : asset.status}
            </span>
            {asset.archivedAt ? <span className="media-item__badge">Archived</span> : null}
          </div>
          <h2>{asset.originalFilename}</h2>
          <p>{asset.alt}</p>
          <div className="media-item__usage-summary">
            {asset.usage.draft > 0 ? (
              <button
                className="media-item__text-action"
                type="button"
                aria-expanded={usagesOpen}
                onClick={() => void toggleUsages()}
              >
                {usagesOpen
                  ? 'Hide usages'
                  : `Used in ${asset.usage.draft} draft${asset.usage.draft === 1 ? '' : 's'}`}
              </button>
            ) : (
              <small>Not used in current content</small>
            )}
            {asset.usage.releases > 0 ? (
              <a className="media-item__text-action" href="/preview">
                {asset.usage.releases} saved preview{asset.usage.releases === 1 ? '' : 's'}
              </a>
            ) : null}
          </div>
          {usagesOpen ? (
            <div className="media-item__usages" aria-label="Current content usages">
              {usagesBusy ? <p className="inline-note">Finding usages…</p> : null}
              {usagesError ? (
                <p className="inline-note" role="alert">
                  Usages could not be loaded. Close and try again.
                </p>
              ) : null}
              {usages?.length === 0 ? (
                <p className="inline-note">No current content usages found.</p>
              ) : null}
              {usages?.map((usage) => (
                <a className="media-item__usage-link" href={usage.href} key={usage.id}>
                  <span>{usage.label}</span>
                  <small>
                    {usage.area} · {usage.context} · {usageLabel(usage)}
                  </small>
                </a>
              ))}
            </div>
          ) : null}
          {editing ? (
            <div className="media-item__editor">
              <TextAreaField
                label="Asset alt text"
                value={draft.alt}
                onChange={(alt) => setDraft({ ...draft, alt })}
              />
              <div className="media-item__actions">
                <Button
                  variant="primary"
                  type="button"
                  disabled={busy || draft.alt.trim().length === 0}
                  onClick={() => void save()}
                >
                  Save media details
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  disabled={busy}
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="media-item__actions">
              <Button
                variant="secondary"
                type="button"
                aria-label={`Edit ${asset.alt}`}
                disabled={busy}
                onClick={() => setEditing(true)}
              >
                Edit details
              </Button>
              {asset.archivedAt ? null : (
                <button
                  className="media-item__text-action media-item__archive"
                  type="button"
                  disabled={busy}
                  onClick={() => void archive()}
                >
                  Archive asset
                </button>
              )}
            </div>
          )}
          <dl className="media-item__metadata">
            <div>
              <dt>File</dt>
              <dd>{asset.originalFilename}</dd>
            </div>
            <div>
              <dt>Dimensions</dt>
              <dd>
                {asset.width ?? '—'} × {asset.height ?? '—'}
              </dd>
            </div>
            <div>
              <dt>Format</dt>
              <dd>{asset.format?.toUpperCase() ?? '—'}</dd>
            </div>
            <div>
              <dt>Source</dt>
              <dd>{asset.sourceType}</dd>
            </div>
            <div>
              <dt>Size</dt>
              <dd>{asset.byteSize === null ? '—' : `${asset.byteSize} bytes`}</dd>
            </div>
            <div>
              <dt>Added</dt>
              <dd>
                <time dateTime={asset.createdAt}>{asset.createdAt.slice(0, 10)}</time>
              </dd>
            </div>
          </dl>
          {message ? (
            <p className="inline-note" role="status">
              {message}
            </p>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function usageLabel(usage: AssetUsageLocation) {
  if (usage.purpose === 'Gallery') return `Gallery image ${usage.position + 1}`;
  return usage.purpose;
}
